import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Label as LabelIcon,
  TrendingUp,
  Category,
  Assessment,
  Timeline,
  CompareArrows,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { advancedReportsAPI } from '../../services/api';
import { ExportButton } from './ExportButton';

interface LabelAnalyticsProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
  selectedAgent?: number | 'all';
  selectedChannel?: number | 'all';
}

interface LabelBreakdown {
  label_id: number;
  label_title: string;
  label_color: string;
  conversation_count: number;
  percentage: number;
  avg_resolution_time: number;
  avg_csat: number;
  resolution_rate: number;
  resolved_count: number;
}

interface LabelTrend {
  date: string;
  total_conversations: number;
  labeled_conversations: number;
  label_counts: { [key: string]: number };
}

interface LabelPerformance {
  label_title: string;
  label_color: string;
  conversation_count: number;
  avg_resolution_time: number;
  avg_csat: number;
  resolution_rate: number;
}

interface LabelCombination {
  label_1: string;
  label_1_color: string;
  label_2: string;
  label_2_color: string;
  count: number;
}

interface LabelByChannel {
  channel: string;
  labels: { label: string; count: number }[];
}

interface LabelByAgent {
  agent: string;
  total_labeled_conversations: number;
  labels: { label: string; count: number; percentage: number }[];
}

interface TopLabel {
  id: number;
  label: string;
  value: number;
  color: string;
}

interface LabelAnalyticsData {
  summary: {
    total_labels: number;
    total_labeled_conversations: number;
    total_conversations: number;
    avg_labels_per_conversation: number;
    unlabeled_conversations: number;
    labeling_rate: number;
    most_used_label: { title: string; count: number; color: string } | null;
    least_used_label: { title: string; count: number; color: string } | null;
  };
  labels_breakdown: LabelBreakdown[];
  label_trend: LabelTrend[];
  label_performance: LabelPerformance[];
  label_combinations: LabelCombination[];
  label_by_channel: LabelByChannel[];
  label_by_agent: LabelByAgent[];
  top_labels: TopLabel[];
}

export const LabelAnalytics: React.FC<LabelAnalyticsProps> = ({
  dateRange,
  selectedAgent,
  selectedChannel
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<LabelAnalyticsData | null>(null);

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange, selectedAgent, selectedChannel]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0]
      };

      if (selectedAgent && selectedAgent !== 'all') {
        params.agent_id = selectedAgent;
      }
      if (selectedChannel && selectedChannel !== 'all') {
        params.channel_id = selectedChannel;
      }

      const data = await advancedReportsAPI.getLabelAnalytics(params);
      setAnalyticsData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch label analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!analyticsData) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No label analytics data available for the selected period.
      </Alert>
    );
  }

  const { summary, labels_breakdown, label_trend, label_performance, label_combinations, label_by_channel, label_by_agent, top_labels } = analyticsData;

  // Prepare export data
  const exportData = [
    {
      total_labels: summary.total_labels,
      total_labeled_conversations: summary.total_labeled_conversations,
      labeling_rate: summary.labeling_rate,
      avg_labels_per_conversation: summary.avg_labels_per_conversation
    },
    ...labels_breakdown
  ];

  // Prepare trend data for chart
  const trendChartData = label_trend.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    total: item.total_conversations,
    labeled: item.labeled_conversations,
    rate: item.total_conversations > 0
      ? Math.round((item.labeled_conversations / item.total_conversations) * 100)
      : 0
  }));

  // Colors for charts
  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FFBB28', '#FF8042', '#0088FE'];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LabelIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5">
            Label Analytics
          </Typography>
        </Box>
        <ExportButton
          data={exportData}
          filename="label_analytics"
          metadata={{ dateRange, filters: { agent: selectedAgent, channel: selectedChannel } }}
        />
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Labels
              </Typography>
              <Typography variant="h4" color="primary">
                {summary.total_labels}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                defined in account
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Labeling Rate
              </Typography>
              <Typography variant="h4" color="success.main">
                {summary.labeling_rate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {summary.total_labeled_conversations} of {summary.total_conversations} conversations
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Avg Labels/Conversation
              </Typography>
              <Typography variant="h4" color="info.main">
                {summary.avg_labels_per_conversation}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                per labeled conversation
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Unlabeled
              </Typography>
              <Typography variant="h4" color="warning.main">
                {summary.unlabeled_conversations}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                conversations without labels
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Most/Least Used Labels */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TrendingUp sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
                Most Used Label
              </Typography>
              {summary.most_used_label ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip
                    label={summary.most_used_label.title}
                    sx={{
                      bgcolor: summary.most_used_label.color,
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                  <Typography variant="h5">
                    {summary.most_used_label.count} conversations
                  </Typography>
                </Box>
              ) : (
                <Typography color="text.secondary">No labels used yet</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Assessment sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
                Least Used Label
              </Typography>
              {summary.least_used_label ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip
                    label={summary.least_used_label.title}
                    sx={{
                      bgcolor: summary.least_used_label.color,
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                  <Typography variant="h5">
                    {summary.least_used_label.count} conversations
                  </Typography>
                </Box>
              ) : (
                <Typography color="text.secondary">No labels used yet</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Label Distribution Pie Chart & Trend */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <PieChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Label Distribution
              </Typography>
              {top_labels.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={top_labels}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }: { name?: string; percent?: number }) => `${name ?? ''} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {top_labels.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No label data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Timeline sx={{ mr: 1, verticalAlign: 'middle' }} />
                Labeling Trend
              </Typography>
              {trendChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={trendChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stackId="1"
                      stroke="#8884d8"
                      fill="#8884d8"
                      name="Total Conversations"
                    />
                    <Area
                      type="monotone"
                      dataKey="labeled"
                      stackId="2"
                      stroke="#82ca9d"
                      fill="#82ca9d"
                      name="Labeled Conversations"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No trend data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Label Performance Table */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <BarChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Label Performance Metrics
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Label</TableCell>
                      <TableCell align="right">Conversations</TableCell>
                      <TableCell align="right">Avg Resolution Time</TableCell>
                      <TableCell align="right">Resolution Rate</TableCell>
                      <TableCell align="right">Avg CSAT</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {label_performance.map((label, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Chip
                            label={label.label_title}
                            size="small"
                            sx={{
                              bgcolor: label.label_color,
                              color: 'white'
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">{label.conversation_count}</TableCell>
                        <TableCell align="right">
                          {label.avg_resolution_time > 0
                            ? `${Math.round(label.avg_resolution_time)} min`
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={label.resolution_rate}
                              sx={{ width: 60, height: 8, borderRadius: 4 }}
                              color={label.resolution_rate >= 80 ? 'success' : label.resolution_rate >= 50 ? 'warning' : 'error'}
                            />
                            <Typography variant="body2">{label.resolution_rate}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {label.avg_csat > 0 ? (
                            <Chip
                              label={label.avg_csat.toFixed(1)}
                              size="small"
                              color={label.avg_csat >= 4 ? 'success' : label.avg_csat >= 3 ? 'warning' : 'error'}
                            />
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {label_performance.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No label performance data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Label Combinations & Channel Distribution */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <CompareArrows sx={{ mr: 1, verticalAlign: 'middle' }} />
                Common Label Combinations
              </Typography>
              {label_combinations.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Label Pair</TableCell>
                        <TableCell align="right">Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {label_combinations.map((combo, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Chip
                                label={combo.label_1}
                                size="small"
                                sx={{ bgcolor: combo.label_1_color, color: 'white' }}
                              />
                              <Typography variant="body2">+</Typography>
                              <Chip
                                label={combo.label_2}
                                size="small"
                                sx={{ bgcolor: combo.label_2_color, color: 'white' }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="right">{combo.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No label combinations found
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Category sx={{ mr: 1, verticalAlign: 'middle' }} />
                Labels by Channel
              </Typography>
              {label_by_channel.length > 0 ? (
                <Box>
                  {label_by_channel.map((channel, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        {channel.channel}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {channel.labels.map((label, idx) => (
                          <Tooltip key={idx} title={`${label.count} conversations`}>
                            <Chip
                              label={`${label.label} (${label.count})`}
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No channel data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Labels by Agent */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
                Label Usage by Agent
              </Typography>
              {label_by_agent.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Agent</TableCell>
                        <TableCell align="right">Total Labels Applied</TableCell>
                        <TableCell>Top Labels Used</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {label_by_agent.slice(0, 10).map((agent, index) => (
                        <TableRow key={index}>
                          <TableCell>{agent.agent}</TableCell>
                          <TableCell align="right">{agent.total_labeled_conversations}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {agent.labels.slice(0, 3).map((label, idx) => (
                                <Chip
                                  key={idx}
                                  label={`${label.label} (${label.percentage}%)`}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No agent label data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Full Label Breakdown Table */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <LabelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Complete Label Breakdown
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Label</TableCell>
                      <TableCell align="right">Conversations</TableCell>
                      <TableCell align="right">% of Total</TableCell>
                      <TableCell align="right">Resolved</TableCell>
                      <TableCell align="right">Avg Resolution (min)</TableCell>
                      <TableCell align="right">Avg CSAT</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {labels_breakdown.map((label, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Chip
                            label={label.label_title}
                            size="small"
                            sx={{
                              bgcolor: label.label_color,
                              color: 'white'
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">{label.conversation_count}</TableCell>
                        <TableCell align="right">{label.percentage}%</TableCell>
                        <TableCell align="right">{label.resolved_count}</TableCell>
                        <TableCell align="right">
                          {label.avg_resolution_time > 0 ? Math.round(label.avg_resolution_time) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {label.avg_csat > 0 ? label.avg_csat.toFixed(1) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {labels_breakdown.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No label data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LabelAnalytics;
